// ─── Matomo analytics ───────────────────────────────────────────────────────
//
// The Matomo Tag Manager container is loaded in public/index.html. That
// container bootstraps the Matomo tracker and the global `window._paq` queue.
// We push our custom-event calls straight onto `_paq` (the Matomo tracking
// API) so they land in Matomo's "Events" report with **no extra Tag Manager
// UI configuration required**. Every call is also mirrored onto the `_mtm`
// dataLayer so anyone managing the container can additionally build MTM
// triggers/tags off the same events if they prefer.
//
// `_paq` is an array until the tracker script loads; pushing onto it before
// then simply buffers the call, which the tracker drains on load. So these
// helpers are always safe to call, even very early in bootstrap.
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
// ─── Audience tracking mechanism ─────────────────────────────────────────────
//
// Audience reporting ("logged-in vs guest", "IDM vs SWP", "MLC vs embedded")
// is attached to each Matomo visit via **Custom Dimensions** (Visit scope).
//
// These IDs MUST match the Visit-scope dimensions configured in Matomo
// (Administration → Websites → Manage → Custom Dimensions). Each dimension
// must also be marked **Active** there or its values are not stored.
var DIMENSION = {
    userStatus: 1, // 'logged-in' | 'guest'
    contentType: 2, // 'IDM' | 'SWP' | 'shared' | 'free'
    openContext: 3, // 'mlc' | 'embedded' | 'embed:<host>'
};
/** Event categories — keep these stable; Matomo reports group by them. */
export var AnalyticsCategory = {
    mathTool: 'Math Tool',
    support: 'Support Material',
    save: 'Save / Label',
    mode: 'View / Edit Mode',
    manipulative: 'Manipulative Visibility',
    content: 'Content', // IDM / SWP open
};
/**
 * Analytics are enabled on the production host ONLY. On every other host
 * (staging, local dev, third-party embeds) all tracking is disabled — the
 * Matomo container is never loaded in index.html, and these helpers no-op so
 * nothing is buffered onto _paq/_mtm either. Keep this host in sync with the
 * loader guard in public/index.html.
 */
var ANALYTICS_HOST = 'multiapp.mathlearningcenter.org';
// Per-load opt-out flag. Set true by `applyMatomoOptOut` when the logged-in
// user's email is on the exclusion list — it suppresses every helper below
// (events, audience context, heartbeat) in addition to the Matomo-level
// `optUserOut` we push. In-memory only, so it never leaks across users on a
// shared browser (a subsequent non-excluded load resets it and calls
// `forgetUserOptOut`).
var optedOut = false;
function trackingEnabled() {
    if (optedOut)
        return false;
    try {
        return window.location.hostname === ANALYTICS_HOST;
    }
    catch (_a) {
        return false;
    }
}
function paq() {
    // Create the queue if the container script hasn't run yet so the call is
    // buffered rather than lost.
    window._paq = window._paq || [];
    return window._paq;
}
function mtm() {
    window._mtm = window._mtm || [];
    return window._mtm;
}
var exclusionsCache = null;
// Normalize a JSON list into a lowercase, de-blanked Set. `stripAt` drops a
// leading '@' so domains may be authored as either "example.org" or
// "@example.org".
function toSet(raw, stripAt) {
    if (stripAt === void 0) { stripAt = false; }
    var arr = Array.isArray(raw) ? raw : [];
    return new Set(arr
        .map(function (e) {
        var s = String(e !== null && e !== void 0 ? e : '').trim().toLowerCase();
        return stripAt && s.startsWith('@') ? s.slice(1) : s;
    })
        .filter(Boolean));
}
function loadExclusions() {
    return __awaiter(this, void 0, void 0, function () {
        var res, body, _a, emails, _b;
        return __generator(this, function (_c) {
            switch (_c.label) {
                case 0:
                    if (exclusionsCache)
                        return [2 /*return*/, exclusionsCache];
                    _c.label = 1;
                case 1:
                    _c.trys.push([1, 6, , 7]);
                    return [4 /*yield*/, fetch("".concat(window.location.origin, "/assets/data/matomoOptOut.json"), {
                            cache: 'no-store',
                        })];
                case 2:
                    res = _c.sent();
                    if (!res.ok) return [3 /*break*/, 4];
                    return [4 /*yield*/, res.json().catch(function () { return null; })];
                case 3:
                    _a = _c.sent();
                    return [3 /*break*/, 5];
                case 4:
                    _a = null;
                    _c.label = 5;
                case 5:
                    body = _a;
                    emails = Array.isArray(body) ? body : body === null || body === void 0 ? void 0 : body.excludedEmails;
                    exclusionsCache = {
                        emails: toSet(emails),
                        domains: toSet(body === null || body === void 0 ? void 0 : body.excludedDomains, true),
                    };
                    return [3 /*break*/, 7];
                case 6:
                    _b = _c.sent();
                    exclusionsCache = { emails: new Set(), domains: new Set() };
                    return [3 /*break*/, 7];
                case 7: return [2 /*return*/, exclusionsCache];
            }
        });
    });
}
// Domain portion of an email identifier (after the last '@'), lowercased.
// Returns '' for a non-email identifier (e.g. a plain username) so it never
// matches a domain.
function emailDomain(id) {
    var at = id.lastIndexOf('@');
    return at >= 0 ? id.slice(at + 1) : '';
}
/**
 * Opt the current user in/out of Matomo based on the exclusion allowlist.
 *
 * Call once per auth resolution with the user's identifier(s) — pass the email
 * (and username as a fallback, in case usernames ARE emails). A user is
 * excluded when any identifier's domain is in `excludedDomains` or the exact
 * identifier is in `excludedEmails`. Excluded users are opted out via
 * `optUserOut`; everyone else gets `forgetUserOptOut` so a lingering opt-out
 * from a previously-excluded user on a shared browser is cleared. No-ops on
 * non-production hosts (nothing is tracked there anyway).
 *
 * Returns true when the user was excluded.
 *
 * NOTE: the email is known only after the async /me auth call, so the Tag
 * Manager container may already have fired the very first pageview by the time
 * this runs. `optUserOut` suppresses all subsequent tracking (events,
 * heartbeats, further pageviews); the single initial pageview can't be
 * retracted client-side.
 */
export function applyMatomoOptOut() {
    var identifiers = [];
    for (var _i = 0; _i < arguments.length; _i++) {
        identifiers[_i] = arguments[_i];
    }
    return __awaiter(this, void 0, void 0, function () {
        var ids, _a, emails_1, domains_1, isExcluded, p, err_1;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0:
                    try {
                        if (window.location.hostname !== ANALYTICS_HOST)
                            return [2 /*return*/, false];
                    }
                    catch (_c) {
                        return [2 /*return*/, false];
                    }
                    _b.label = 1;
                case 1:
                    _b.trys.push([1, 3, , 4]);
                    ids = identifiers
                        .map(function (id) { return id === null || id === void 0 ? void 0 : id.trim().toLowerCase(); })
                        .filter(function (id) { return !!id; });
                    return [4 /*yield*/, loadExclusions()];
                case 2:
                    _a = _b.sent(), emails_1 = _a.emails, domains_1 = _a.domains;
                    isExcluded = ids.some(function (id) { return emails_1.has(id) || domains_1.has(emailDomain(id)); });
                    optedOut = isExcluded;
                    p = paq();
                    p.push([isExcluded ? 'optUserOut' : 'forgetUserOptOut']);
                    return [2 /*return*/, isExcluded];
                case 3:
                    err_1 = _b.sent();
                    console.warn('applyMatomoOptOut failed', err_1);
                    return [2 /*return*/, false];
                case 4: return [2 /*return*/];
            }
        });
    });
}
/**
 * Record a custom event. Mirrors to the MTM dataLayer as `customEvent`.
 *
 * @param category one of AnalyticsCategory
 * @param action   what happened (e.g. 'open', 'hide', 'save')
 * @param name     optional label (e.g. the tool id or activity id)
 * @param value    optional numeric value; defaults to 1 so Matomo's
 *                 "Total Event value" sums to the event count (and "Avg value"
 *                 is 1) rather than showing "-". Matomo requires a name before
 *                 a value, so we always send a name too (empty string if none).
 */
export function trackEvent(category, action, name, value) {
    if (value === void 0) { value = 1; }
    if (!trackingEnabled())
        return;
    try {
        // Matomo's positional API requires a name slot to be present before the
        // value slot, so fall back to '' when no name is supplied.
        var event_1 = ['trackEvent', category, action, name !== null && name !== void 0 ? name : '', value];
        paq().push(event_1);
        mtm().push({ event: 'customEvent', eventCategory: category, eventAction: action, eventName: name, eventValue: value });
    }
    catch (err) {
        // Analytics must never break the app.
        console.warn('trackEvent failed', err);
    }
}
/** Detect whether the activity is running inside the MLC app or embedded elsewhere. */
function detectOpenContext() {
    try {
        var embedded = window.self !== window.top;
        if (!embedded)
            return 'mlc';
        var ref = document.referrer ? new URL(document.referrer).hostname : '';
        return ref ? "embed:".concat(ref) : 'embedded';
    }
    catch (_a) {
        // Cross-origin access to window.top throws → we're embedded.
        return 'embedded';
    }
}
/**
 * Set the visit-scoped audience context. Call once per load as soon as the
 * auth state and content type are known. Sets custom dimensions (so every
 * subsequent event/pageview/heartbeat is segmentable) and mirrors to MTM.
 */
export function setAudienceContext(opts) {
    var _a;
    if (!trackingEnabled())
        return;
    try {
        var userStatus = opts.isLoggedIn ? 'logged-in' : 'guest';
        var openContext = detectOpenContext();
        var p = paq();
        // Visit-scoped custom dimensions — tag the whole visit so every
        // subsequent event/pageview/heartbeat is segmentable by audience.
        p.push(['setCustomDimension', DIMENSION.userStatus, userStatus]);
        p.push(['setCustomDimension', DIMENSION.contentType, opts.contentType]);
        p.push(['setCustomDimension', DIMENSION.openContext, openContext]);
        if (typeof opts.userId === 'number')
            p.push(['setUserId', String(opts.userId)]);
        mtm().push({
            event: 'audienceContext',
            userStatus: userStatus,
            contentType: opts.contentType,
            openContext: openContext,
            userId: (_a = opts.userId) !== null && _a !== void 0 ? _a : null,
        });
    }
    catch (err) {
        console.warn('setAudienceContext failed', err);
    }
}
/**
 * One-time analytics setup. Enables the heart-beat timer so Matomo records
 * accurate time-on-page (important for the IDM/SWP "time on page" metric — an
 * activity's URL is unique per revision, so time is attributed per activity).
 */
export function initAnalytics() {
    if (!trackingEnabled())
        return;
    try {
        var p = paq();
        // 15s accuracy is Matomo's recommended default.
        p.push(['enableHeartBeatTimer', 15]);
    }
    catch (err) {
        console.warn('initAnalytics failed', err);
    }
}
